import React, { useState, useEffect } from 'react';
import { extractErrorMessage } from '../lib/errors';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Plus, Edit2, Trash2, FileText } from 'lucide-react';
import { toast } from 'sonner';
import api from '../api';

export default function Templates() {
  const [templates, setTemplates] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [isNew, setIsNew] = useState(false);

  const load = () => api.get('/templates').then(r => setTemplates(r.data)).catch(()=>{});
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing({ name:'', subject:'', body:'' }); setIsNew(true); setOpen(true); };
  const openEdit = (t) => { setEditing({...t}); setIsNew(false); setOpen(true); };
  const save = async () => {
    if (!editing.name) return toast.error('Name required');
    try {
      if (isNew) await api.post('/templates', { name: editing.name, subject: editing.subject||'', body: editing.body||'' });
      else await api.patch(`/templates/${editing.id}`, { name: editing.name, subject: editing.subject, body: editing.body });
      setOpen(false); toast.success('Template saved'); load();
    } catch (err) { toast.error('Save failed'); }
  };
  const remove = async (id) => { await api.delete(`/templates/${id}`); toast.success('Template deleted'); load(); };

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl">Templates</h1>
          <p className="text-zinc-500 mt-1">Reusable email templates with personalization variables.</p>
        </div>
        <Button onClick={openNew} className="bg-[#d4a94a] hover:bg-[#e5bb5c] text-black font-medium"><Plus size={16} className="mr-1"/>New template</Button>
      </div>
      {templates.length === 0 ? (
        <Card className="mt-8 bg-[#111110] border-zinc-900 p-12 text-center">
          <div className="font-display text-xl">No templates yet</div>
          <p className="text-sm text-zinc-500 mt-2">Save your best emails as templates and reuse them in campaigns.</p>
          <Button className="mt-6 bg-[#d4a94a] hover:bg-[#e5bb5c] text-black font-medium" onClick={openNew}>Create template</Button>
        </Card>
      ) : (
        <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.map(t => (
            <Card key={t.id} className="bg-[#111110] border-zinc-900 p-5 flex flex-col">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-lg bg-[#1a1815] border border-[#3a2f18] flex items-center justify-center text-[#f5d67a]"><FileText size={16}/></div>
                <div className="font-display text-lg flex-1 truncate">{t.name}</div>
              </div>
              <div className="mt-4 text-sm text-zinc-300 font-medium truncate">{t.subject}</div>
              <div className="mt-2 text-xs text-zinc-500 line-clamp-3 whitespace-pre-wrap">{t.body}</div>
              <div className="mt-4 pt-3 border-t border-zinc-900 flex items-center justify-between text-xs text-zinc-500">
                <span>Updated {t.updated_at?.slice(0,10)}</span>
                <div className="flex gap-2">
                  <button onClick={()=>openEdit(t)} className="hover:text-[#f5d67a]"><Edit2 size={14}/></button>
                  <button onClick={()=>remove(t.id)} className="hover:text-red-400"><Trash2 size={14}/></button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#111110] border-zinc-800 text-zinc-100 max-w-2xl">
          <DialogHeader><DialogTitle className="font-display">{isNew ? 'New template' : 'Edit template'}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <Input placeholder="Template name" value={editing.name} onChange={e=>setEditing({...editing, name:e.target.value})} className="bg-[#0a0a0b] border-zinc-800 text-zinc-100 focus-visible:ring-[#d4a94a]"/>
              <Input placeholder="Subject line" value={editing.subject||''} onChange={e=>setEditing({...editing, subject:e.target.value})} className="bg-[#0a0a0b] border-zinc-800 text-zinc-100 focus-visible:ring-[#d4a94a]"/>
              <Textarea rows={9} placeholder="Body..." value={editing.body||''} onChange={e=>setEditing({...editing, body:e.target.value})} className="bg-[#0a0a0b] border-zinc-800 text-zinc-100 focus-visible:ring-[#d4a94a] font-mono text-sm"/>
              <div className="text-xs text-zinc-500">Variables: {'{{first_name}}, {{last_name}}, {{company}}, {{job_title}}'}</div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="border-zinc-800 text-zinc-200 hover:bg-zinc-900" onClick={()=>setOpen(false)}>Cancel</Button>
            <Button className="bg-[#d4a94a] hover:bg-[#e5bb5c] text-black" onClick={save}>Save template</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
